import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { OverlayModule } from 'primeng/overlay';

import { UserInfo } from '@core/models/interfaces/auth';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppState } from '@state/app.state';
import { selectUserInfo } from '@state/auth/auth.selectors';
import { setIsSidebar } from '@state/system/system.actions';

import { FormsModule } from '@angular/forms';
import { BANNER_LOCATION } from '@core/enum/admin-content';
import { BannerDetail } from '@core/models/interfaces/admin-content/admin-content';
import { BannerService } from '@core/services/amin-content/banner.service';
import { AuthService } from '@core/services/auth.service';
import { LeadSearchService } from '@core/services/lead-search.service';
import { Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CustomAvatarComponent } from '../custom-avatar/custom-avatar.component';
import { CustomImageComponent } from '../custom-image/custom-image.component';
import { FlagsComponent } from '../flags/flags.component';
import { NotificationComponent } from '../notification/notification.component';
import { logout } from '@state/auth/auth.actions';
import { FcmService } from '@core/services/firebase-notification.service';
import { StorageService } from '@core/services/storage.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    RouterModule,
    BadgeModule,
    TranslateModule,
    AvatarModule,
    CommonModule,
    FlagsComponent,
    OverlayModule,
    CustomAvatarComponent,
    NotificationComponent,
    CustomImageComponent,
    FormsModule
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit {
  @ViewChild('flags') flagComponent: FlagsComponent;

  readonly environment = environment;
  readonly unsubscribe$ = new Subject();
  readonly translatePrefix = 'lead-management.';

  SMALL_SCREEN = 1280;
  isSmallScreen = false;
  visible = false;
  userInfo: UserInfo;
  keyword: string;

  logo: { data?: BannerDetail; loading: boolean } = {
    data: undefined,
    loading: false
  };

  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService,
    private bannerService: BannerService,
    private authService: AuthService,
    private leadSearchService: LeadSearchService,
    private fcmService: FcmService,
    private router: Router,
    private storageService: StorageService
  ) {}

  @HostListener('window:resize', ['$event'])
  checkSmallScreen() {
    this.isSmallScreen = window.innerWidth <= this.SMALL_SCREEN;
  }

  @HostListener('window:scroll', ['$event'])
  scroll() {
    this.visible = false;
  }

  ngOnInit(): void {
    this.checkSmallScreen();
    this.fetchLogo();

    this.bannerService.logo$.pipe(takeUntil(this.unsubscribe$)).subscribe(logo => {
      this.logo = logo;
    });

    this.store.select(selectUserInfo).subscribe(userInfo => {
      if (userInfo) {
        this.userInfo = userInfo;
        if (this.flagComponent) {
          const lang =
            this.flagComponent.languages.find(el => el.language === userInfo.language) ||
            this.flagComponent.languages[0];
          this.flagComponent.selectedLanguage = lang;
          this.translateService.use(lang?.language);
        }
      }
    });
  }

  setSidebar() {
    this.store.dispatch(setIsSidebar({ isSidebar: true }));
  }

  async onSignOut() {
    try {
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
  
        for (const registration of registrations) {
          try {
            const sub = await registration.pushManager.getSubscription();
  
            if (sub) {
              await sub.unsubscribe();
            }
          } catch (err) {}
  
          await registration.unregister();
        }
      }
  
      const fcmToken = localStorage.getItem("fcm_token");
  
      if (fcmToken) {
        this.fcmService.unregisterToken(fcmToken).subscribe();
        await this.fcmService.deleteFCMToken();
      }
  
      const token = this.authService.getLocalToken();
  
      if (token) {
        this.store.dispatch(logout({ token }));
      }
  
      localStorage.removeItem("fcm_token");

    } catch (error) {}
  }

  onKeydownEvent(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.router.navigate(['/lead-management']);
      this.leadSearchService.onSearchLeads(this.keyword, true);
    }
  }

  fetchLogo() {
    this.bannerService
      .getBannerManagement()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: res => {
          const logoData = {
            data: res?.data?.content.find(el => el.location.replace(/\s+/g, '').toUpperCase() === BANNER_LOCATION.LOGO),
            loading: false
          };
          this.bannerService.updateLogo(logoData);
          if (logoData.data?.attachment) {
            const logoUrl = this.storageService.getFileUrl(logoData.data?.attachment)
            this.saveLogo(logoUrl);
          }
        }
      });
  }

  async saveLogo(logoUrl: string) {
    if ('caches' in window) {
      const cache = await caches.open('app-config');
      const response = new Response(JSON.stringify({ logo: logoUrl }));
      await cache.put('/logo-config', response);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next('Complete');
    this.unsubscribe$.complete();
  }
}
